/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import {
  browserName,
  BrowserTypes,
  isMobile,
  osName,
  OsTypes,
  isLegacyEdge,
  isEdgeChromium,
} from 'react-device-detect';
import AppConfig from '../AppConfig';

export enum APP_BROWSERS {
  Chrome = 'Chrome',
  Firefox = 'Firefox',
  Opera = 'Opera',
  Safari = 'Safari',
  InternetExplorer = 'InternetExplorer',
  Edge = 'Edge',
  Chromium = 'Chromium',
  MobileSafari = 'MobileSafari',
  EdgeChromium = 'EdgeChromium',
  Unknown = 'Unknown',
}
export enum APP_OS {
  IOS = 'IOS',
  Android = 'Android',
  Windows = 'Windows',
  MAC_OS = 'MAC_OS',
  Unknown = 'Unknown',
}

enum DMS_BROWSERS {
  Chrome = 'chrome',
  InternetExplorer = 'ie',
  Edge = 'edge', // Legacy Edge
  Opera = 'opera',
  Safari = 'safari',
  Firefox = 'firefox',
  EdgeChromium = 'edge', // Chromium based Edge
}

enum DMS_OS {
  Windows = 'pc',
  MAC_OS = 'mac',
  Unix = 'pc',
  Linux = 'pc',
}

export default class PlatformUtils {
  static getBrowser(): APP_BROWSERS {
    if (isEdgeChromium) {
      return APP_BROWSERS.EdgeChromium;
    }
    if (isLegacyEdge) {
      return APP_BROWSERS.Edge;
    }
    switch (browserName) {
      case BrowserTypes.Chrome:
        return APP_BROWSERS.Chrome;
      case BrowserTypes.Chromium:
        return APP_BROWSERS.Chromium;
      case BrowserTypes.Firefox:
        return APP_BROWSERS.Firefox;
      case BrowserTypes.Ie:
        return APP_BROWSERS.InternetExplorer;
      case BrowserTypes.InternetExplorer:
        return APP_BROWSERS.InternetExplorer;
      case BrowserTypes.MobileSafari:
        return APP_BROWSERS.MobileSafari;
      case BrowserTypes.Opera:
        return APP_BROWSERS.Opera;
      case BrowserTypes.Safari:
        return APP_BROWSERS.Safari;
      default:
        return APP_BROWSERS.Unknown;
    }
  }

  static getOS(): APP_OS {
    switch (osName) {
      case OsTypes.Android:
        return APP_OS.Android;
      case OsTypes.IOS:
        return APP_OS.IOS;
      case OsTypes.MAC_OS:
        return APP_OS.MAC_OS;
      case OsTypes.Windows:
        return APP_OS.Windows;
      default:
        return APP_OS.Unknown;
    }
  }

  static getDmsBrowser(): DMS_BROWSERS | undefined {
    if (isEdgeChromium) {
      return DMS_BROWSERS.EdgeChromium;
    }
    if (isLegacyEdge) {
      return DMS_BROWSERS.Edge;
    }
    switch (browserName) {
      case BrowserTypes.Chrome:
        return DMS_BROWSERS.Chrome;
      case BrowserTypes.Chromium:
        return DMS_BROWSERS.Chrome;
      case BrowserTypes.Firefox:
        return DMS_BROWSERS.Firefox;
      case BrowserTypes.Ie:
        return DMS_BROWSERS.InternetExplorer;
      case BrowserTypes.InternetExplorer:
        return DMS_BROWSERS.InternetExplorer;
      case BrowserTypes.Safari:
        return DMS_BROWSERS.Safari;
      default:
        return undefined;
    }
  }

  static getDmsOS(): DMS_OS | undefined {
    switch (osName) {
      case OsTypes.MAC_OS:
        return DMS_OS.MAC_OS;
      case OsTypes.Windows:
        return DMS_OS.Windows;
      default:
        return undefined;
    }
  }

  static isSupportedPlatform(): boolean {
    if (AppConfig.env.DMS_ALLOWED_APP_NAME_COMBOS === undefined) {
      return true;
    }
    const browser = this.getDmsBrowser();
    const os = this.getDmsOS();
    if (
      browser &&
      os &&
      AppConfig.env.DMS_ALLOWED_APP_NAME_COMBOS.length &&
      AppConfig.env.DMS_ALLOWED_APP_NAME_COMBOS.includes(`${os}.${browser}`)
    ) {
      return true;
    }
    return false;
  }

  static replacePlatformVars(input: string): string | undefined {
    const browser = this.getDmsBrowser();
    const os = this.getDmsOS();
    if (os && browser) {
      return input.replace('{os}', os).replace('{browser}', browser);
    }
    return undefined;
  }

  static isMobileDevice(): boolean {
    return isMobile;
  }
}
